import {useTranslation} from "react-i18next";
import {useOrderPreviewContext} from "../OrderPreviewContext";
import FlintLabelBox from "../../../../components/controls/FlintLabelBox";
import {toString as kendoToString} from "@progress/kendo-intl"
import {parseISO} from "date-fns";
import FlintDateTimePicker from "../../../../components/controls/FlintDateTimePicker";
import {useEffect, useMemo, useState} from "react";
import {DateTimePickerChangeEvent} from "@progress/kendo-react-dateinputs";
import FlintTextArea from "../../../../components/controls/FlintTextArea";
import {TextAreaChangeEvent} from "@progress/kendo-react-inputs";
import {
    OrderPreviewBasicDataModel,
    OrderPreviewModel, OrderPreviewModelStateType,
    VivoPanelModel,
    StartWorkRequest
} from "../../order.models";
import FlintButton from "../../../../components/controls/FlintButton";
import FlintLoadingWrapper from "../../../../components/page_components/common/loading/FlintLoadingWrapper";
import {WindowActionsBar} from "@progress/kendo-react-dialogs";
import {ApiUrl_Orders_StartWork} from "../../../../utils/Endpoints";
import axios, {AxiosError} from "axios";
import {
    useFlintNotificationContext
} from "../../../../components/page_components/common/notifications/FlintNotificationContext";
import {ApiError} from "../../../../utils/errors/ApiError";
import {ApiResponseDto, BasePopupProps} from "../../../../utils/Models";
import {Hint} from "@progress/kendo-react-labels";

export default function StartJobConfirmationPanel(props: StartJobConfirmationPanelProps) {
    const {t} = useTranslation();
    const localTimeZOneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const {state, handlers} = useStartJobConfirmationPanel(props);
    return (
        <FlintLoadingWrapper showLoading={state.isLoadingState.get} loadingWrapperClassName={"d-grid h-100"}>
            <div className="alert alert-info text-center">{t("lblStartWorkInfo")}</div>
            <div className={"form-group"}>
                <FlintLabelBox title={t("ORDER_DATE_REQUESTED")} className={"d-flex justify-content-between"}
                               text={kendoToString(state.requestedDateTimeState.get, "yyyy-MM-dd HH:mm")}/>
                <Hint className="text-end d-block">({localTimeZOneName})</Hint>
            </div>
            <div className={"form-group"}>
                <FlintDateTimePicker label={t("lblConfirmedDate")} value={state.selectedConfirmedDateState.get}
                                     onChange={handlers.handleSelectedConfirmedDateChanged}/>
                <Hint className="text-end d-block">{localTimeZOneName}</Hint>
            </div>
            {state.delayReasonVisibleState.get && <div className={"form-group"}>
                <FlintTextArea label={t("lblDelayReasonPrompt")} maxLength={1000} rows={3}
                               value={state.delayReasonState.get} onChange={handlers.handleDelayReasonChanged}/>
            </div>}
            <WindowActionsBar layout={"center"}>
                <FlintButton disabled={state.isLoadingState.get} themeColor={"secondary"}
                             onClick={handlers.handleCancelled}>{t("Cancel")}</FlintButton>
                <FlintButton disabled={state.isLoadingState.get} themeColor={"primary"}
                             onClick={handlers.handleConfirmed}>{t("Confirm")}</FlintButton>
            </WindowActionsBar>
        </FlintLoadingWrapper>
    )
}

export interface StartJobConfirmationPanelProps extends BasePopupProps {
    orderPreviewModelState: OrderPreviewModelStateType
}

function useStartJobConfirmationPanel(props: StartJobConfirmationPanelProps) {
    const [selectedConfirmedDate, setSelectedConfirmedDate] = useState<Date | null>();
    const [delayReason, setDelayReason] = useState<string>();
    const [delayReasonVisible, setDelayReasonVisible] = useState<boolean>(false);
    const basicDataState = props.orderPreviewModelState.get.OrderBasicData;
    const requestedDateTime = useMemo(() => parseISO(basicDataState.DueDateTimeUtc + 'Z'), []);
    const [isLoading, setIsLoading] = useState(false);
    const {showNotification} = useFlintNotificationContext();
    const {t} = useTranslation();
    
    useEffect(() => {
        setDelayReasonVisible(selectedConfirmedDate != null && requestedDateTime < selectedConfirmedDate);
    }, [selectedConfirmedDate])
    useEffect(() => {
        let confirmedDate = parseISO((basicDataState.ConfirmedDateUtc ?? basicDataState.DueDateTimeUtc) + 'Z');
        if (!confirmedDate)
            confirmedDate = parseISO(basicDataState.DueDateTimeUtc + 'Z');
        setSelectedConfirmedDate(confirmedDate);
    }, []);

    function handleSelectedConfirmedDateChanged(event: DateTimePickerChangeEvent) {
        setSelectedConfirmedDate(event.value);
    }

    function handleDelayReasonChanged(event: TextAreaChangeEvent) {
        setDelayReason(event.value);
    }

    function handleCancelled() {
        props.OnCancelled();
    }

    function handleConfirmed() {
        if (isLoading)
            return;
        setIsLoading(true);
        const post: StartWorkRequest = {
            OrderGuid: basicDataState.Guid,
            ConfirmedDateUtc: selectedConfirmedDate ?? basicDataState.DueDateTimeUtc,
            DelayReason: delayReason
        }
        return axios.post<ApiResponseDto<OrderPreviewModel>>(ApiUrl_Orders_StartWork, post)
            .then(res => {
                props.orderPreviewModelState.setModel(res.data.Result);
                showNotification(t("ORDER_BEING_PROCESSED"), {style: "success"});
                props.OnConfirmedAndSaved();
            })
            .catch((err: ApiError) => {
                showNotification(err)
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    return {
        state: {
            selectedConfirmedDateState: {get: selectedConfirmedDate},
            delayReasonState: {get: delayReason},
            delayReasonVisibleState: {get: delayReasonVisible},
            requestedDateTimeState: {get: requestedDateTime},
            isLoadingState: {get: isLoading}
        },
        handlers: {handleSelectedConfirmedDateChanged, handleDelayReasonChanged, handleCancelled, handleConfirmed},
    }
}